import { useEffect, useState } from "react";
import {
  getApiHealth,
  postVetcanChat,
  type ClinicProfileResponse,
  type VetcanToolResult,
} from "../lib/vetcanApi";
import { type WhiteLabelTenantConfig } from "../lib/tenantConfig";

type ChatPanelMode = "widget" | "full" | "embed";
type ChatMessageKind = "text" | "action" | "error";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  kind?: ChatMessageKind;
  action?: string;
  referenceId?: string;
  escalate?: boolean;
};

const quickReplyOptions = [
  "Hours",
  "Location",
  "Parking",
  "Schedule appointment",
  "Renewals",
  "Call or text us",
];

const makeId = (prefix: string) => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
};

const sanitizeClinicCopy = (value: string) =>
  value
    .replace(/\bvetcan\b/gi, "clinic support")
    .replace(/\ba\s*storm\b/gi, "clinic support");

const formatToolAction = (action?: string) => {
  if (!action) return "Update";
  return action
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const hasEscalation = (results?: VetcanToolResult[]) =>
  Array.isArray(results) &&
  results.some((result) => /escalat|handoff|staff/i.test(result.action || ""));

const buildInitialMessages = (
  clinicName: string,
  renewalsPhone: string,
  supportPhone: string,
  noMedicalAdviceNotice: string
): ChatMessage[] => [
  {
    id: makeId("msg"),
    role: "assistant",
    kind: "text",
    content: `Welcome to ${clinicName}. I can help with hours, location, parking, first-contact booking, renewals, and staff handoff when needed.`,
  },
  {
    id: makeId("msg"),
    role: "assistant",
    kind: "text",
    content: `${noMedicalAdviceNotice} When payment is required after booking, secure payment options are used. Renew by telephone (no video): ${renewalsPhone}. Call or text 24/7: ${supportPhone}.`,
  },
];

export default function WhiteLabelChatPanel({
  tenant,
  tenantKey,
  clinicProfile,
  profileLoading,
  mode = "full",
}: {
  tenant: WhiteLabelTenantConfig;
  tenantKey?: string;
  clinicProfile: ClinicProfileResponse | null;
  profileLoading?: boolean;
  mode?: ChatPanelMode;
}) {
  const clinicName = clinicProfile?.displayName || tenant.clinicName;
  const renewalsPhone = clinicProfile?.renewalsPhone || tenant.renewalsPhone;
  const supportPhone = clinicProfile?.supportPhone || tenant.supportPhone;
  const noMedicalAdviceNotice =
    clinicProfile?.noMedicalAdviceNotice || tenant.noMedicalAdviceNotice;

  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    buildInitialMessages(
      clinicName,
      renewalsPhone,
      supportPhone,
      noMedicalAdviceNotice
    )
  );
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState(() =>
    makeId("conversation")
  );
  const [lastCorrelationId, setLastCorrelationId] = useState<string>("n/a");
  const [isApiConnected, setIsApiConnected] = useState(false);
  const canSend = input.trim().length > 0 && !isSending;
  const chatHeightClass =
    mode === "embed" ? "h-[72vh]" : mode === "widget" ? "h-80" : "h-[60vh]";

  useEffect(() => {
    setMessages((prev) => {
      const hasUserMessage = prev.some((message) => message.role === "user");
      if (hasUserMessage) return prev;
      return buildInitialMessages(
        clinicName,
        renewalsPhone,
        supportPhone,
        noMedicalAdviceNotice
      );
    });
  }, [clinicName, renewalsPhone, supportPhone, noMedicalAdviceNotice]);

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        await getApiHealth();
        if (!active) return;
        setIsApiConnected(true);
      } catch {
        if (!active) return;
        setIsApiConnected(false);
      }
    };
    void check();
    return () => {
      active = false;
    };
  }, []);

  const sendMessage = async (overrideInput?: string) => {
    const outbound = (overrideInput ?? input).trim();
    if (!outbound || isSending) return;
    const correlationId = makeId("corr");
    const userMessage: ChatMessage = {
      id: makeId("msg"),
      role: "user",
      kind: "text",
      content: outbound,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsSending(true);

    try {
      const payload = await postVetcanChat({
        conversationId,
        correlationId,
        message: outbound,
      }, tenantKey);
      setConversationId(payload.conversationId || conversationId);
      setLastCorrelationId(payload.correlationId || correlationId);

      const toolMessages: ChatMessage[] = Array.isArray(payload.toolResults)
        ? payload.toolResults.map((result) => ({
            id: makeId("msg"),
            role: "assistant",
            kind: result.ok ? "action" : "error",
            action: result.action,
            referenceId: result.entityId || result.correlationId,
            content: sanitizeClinicCopy(result.message),
          }))
        : [];

      const assistantMessage: ChatMessage = {
        id: makeId("msg"),
        role: "assistant",
        kind: "text",
        escalate: payload.escalate || hasEscalation(payload.toolResults),
        content: sanitizeClinicCopy(
          payload.reply ||
            `Thanks. We can keep this moving here, and staff can step in if needed.`
        ),
      };

      setMessages((prev) => [...prev, assistantMessage, ...toolMessages]);
    } catch {
      setLastCorrelationId(correlationId);
      setMessages((prev) => [
        ...prev,
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "error",
          content:
            `We could not reach chat right now. Please try again. Call or text 24/7: ${supportPhone}. Renew by telephone (no video): ${renewalsPhone}.`,
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section
      className={`rounded-3xl border border-emerald-200 bg-white shadow-md ${
        mode === "embed" ? "p-4" : "p-6"
      }`}
    >
      {mode !== "embed" && (
        <header className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-emerald-900">
              {clinicName} Chat
            </h2>
            <p className="text-sm text-slate-600">
              Ask about hours, location, parking, scheduling, renewals, or
              staff handoff.
            </p>
            <p className="text-xs text-slate-500">{noMedicalAdviceNotice}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              Live
            </span>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                isApiConnected
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-amber-100 text-amber-800"
              }`}
            >
              {isApiConnected ? "Connected" : "Offline"}
            </span>
          </div>
        </header>
      )}

      {profileLoading && (
        <p className="mb-2 text-xs text-slate-500">Loading clinic profile...</p>
      )}

      <p className="mb-3 break-all text-[11px] text-slate-500">
        Conversation: {conversationId} | Last correlation: {lastCorrelationId}
      </p>

      <div className="mb-3 flex flex-wrap gap-2">
        {quickReplyOptions.map((option) => (
          <button
            key={option}
            type="button"
            className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-900 transition hover:bg-emerald-100"
            onClick={() => void sendMessage(option)}
            disabled={isSending}
          >
            {option}
          </button>
        ))}
      </div>

      <div className={`${chatHeightClass} space-y-3 overflow-y-auto rounded-2xl bg-slate-50 p-4`}>
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[84%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                message.role === "user"
                  ? "bg-emerald-700 text-white"
                  : message.kind === "action"
                    ? "border border-emerald-200 bg-emerald-50 text-emerald-900"
                    : message.kind === "error"
                      ? "border border-amber-200 bg-amber-50 text-amber-900"
                      : "bg-white text-slate-800"
              }`}
            >
              {message.kind === "action" ? (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Action logged
                  </p>
                  <p className="font-semibold">{formatToolAction(message.action)}</p>
                  <p>{message.content}</p>
                  {message.referenceId && (
                    <p className="text-xs text-slate-500">
                      Reference ID: {message.referenceId}
                    </p>
                  )}
                </div>
              ) : (
                <p className="whitespace-pre-line">{message.content}</p>
              )}
              {message.escalate && (
                <p className="mt-2 text-xs text-emerald-700">
                  Clinic staff follow-up has been requested.
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Type your question..."
          className="flex-1 rounded-full border border-slate-300 px-4 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              void sendMessage();
            }
          }}
        />
        <button
          type="button"
          onClick={() => void sendMessage()}
          disabled={!canSend}
          className="rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {isSending ? "Sending..." : "Send"}
        </button>
      </div>
    </section>
  );
}
